import { computed, onScopeDispose, ref, shallowRef, watch } from 'vue'
import { defineStore } from 'pinia'
import CryptoJS from 'crypto-js'
import useUserStore from './user'
import ImageServer, { putEnterpriseImage } from '~/services/EnterpriseImageServer'
import {
  emptyImageOptions, enterpriseImageUrl, imageEntitlementKey, imageFileKey, imageMime,
  imagePollVerdict, minimumImageCost, parseImagePromptOptions, readImageBilling,
  validateImageBytes, validateImageDimensions, IMAGE_MAX_BYTES,
  type ImagePromptGroup, type ImageStatus,
} from '~/utils/enterpriseImage'

const HIGH_RESOLUTION_PREFERENCE = 'enterprise-image-auto-high-resolution'

/** 不持久化到 localStorage：站内切换保留任务；刷新从记录重新查看。 */
export default defineStore('enterprise-image', () => {
  const user = useUserStore()
  const publicConfig = useRuntimeConfig().public
  const identity = computed(() => user.token && user.isUserInfoReady && user.userInfo?.id && user.userInfo?.bizTeamId
    ? `${publicConfig.apiUrl}:${user.token}:${user.userInfo.id}:${user.userInfo.bizTeamId}` : '')
  const status = ref<ImageStatus>('idle')
  const progress = ref(0)
  const file = shallowRef<File | null>(null)
  const originName = ref('')
  const sourceUrl = ref('')
  const sourceBlobName = ref('')
  const resultBlobName = ref('')
  const resultVersion = ref(0)
  const operateRecordId = ref(0)
  const dimensions = ref({ width: 0, height: 0 })
  const isHighResolutionSource = ref(false)
  const pendingHighResolution = ref<{ width: number; height: number } | null>(null)
  const sourceLang = ref('')
  const targetLang = ref('en')
  const selected = ref({ scene: '', fidelity: '', repaint: '' })
  const ocrEnhance = ref(false)
  const options = ref(emptyImageOptions())
  const configLoading = ref(false)
  const configLoaded = ref(false)
  const configError = ref(false)
  const profiles = ref<Record<string, any>>({})
  const errorKey = ref('')
  const validating = ref(false)
  const submissionUnknown = ref(false)
  const pollingInterrupted = ref(false)
  let fileKey = ''
  let digest = ''
  let epoch = 0
  let taskController = new AbortController()
  let configController = new AbortController()
  let timer: ReturnType<typeof setTimeout> | undefined
  let pendingFile: File | null = null
  let pendingDigest = ''
  let initialized = false
  let configPromise: Promise<void> | null = null

  const team = computed(() => user.bizTeamInfo as Record<string, any>)
  const billing = computed(() => readImageBilling(profiles.value[imageEntitlementKey(team.value) || '']))
  const remainingWords = computed(() => Math.max(0, Number(team.value.remainServiceWords) || 0) + Math.max(0, Number(team.value.remainPackageWords) || 0))
  const configReady = computed(() => configLoaded.value && !configError.value && Object.values(options.value).every(group => group.length > 0) && !!billing.value)
  const blockedKey = computed(() => {
    if (!user.token) return 'bizImage_login'
    if (!user.isUserInfoReady) return 'bizImage_loading'
    if (!user.userInfo?.bizTeamId) return 'bizImage_no_team'
    if (String(team.value.id || '') !== String(user.userInfo.bizTeamId)) return 'bizImage_loading'
    if (!(Number(team.value.serviceLevel) > 0) || !(new Date(team.value.serviceEndTime).getTime() > Date.now())) return 'bizImage_no_plan'
    if (Number(team.value.serviceLevel) >= 5 && team.value.globalContractNo && !(Number(team.value.globalAllocationVersion) > 0)) return 'bizImage_allocation'
    if (configLoading.value || !configLoaded.value) return 'bizImage_loading'
    if (configError.value) return 'bizImage_err_config'
    if (!Object.values(options.value).every(group => group.length > 0)) return 'bizImage_no_prompts'
    if (!billing.value) return 'bizImage_unconfigured'
    if (remainingWords.value <= 0 || remainingWords.value < minimumImageCost(billing.value)) return 'bizImage_balance'
    return ''
  })
  const isBusy = computed(() => validating.value || status.value === 'uploading' || status.value === 'translating')
  const canUpload = computed(() => !isBusy.value && !pendingHighResolution.value && !blockedKey.value)
  const validSelection = computed(() => (Object.keys(options.value) as ImagePromptGroup[]).every(group => options.value[group].some(option => option.value === selected.value[group])))
  const canStart = computed(() => !isBusy.value && !blockedKey.value && !submissionUnknown.value && !!fileKey && !!targetLang.value
    && validSelection.value && ['uploaded', 'fail'].includes(status.value))
  const resultUrl = computed(() => enterpriseImageUrl(String(publicConfig.downloadUrl || ''), resultBlobName.value, true, resultVersion.value))

  function current(generation: number) { return generation === epoch && !taskController.signal.aborted && !!identity.value }
  function fail(key: string) { status.value = 'fail'; errorKey.value = key }
  function stopTask() {
    epoch += 1
    taskController.abort()
    taskController = new AbortController()
    clearTimeout(timer)
    timer = undefined
  }
  function clearTask() {
    stopTask()
    if (sourceUrl.value.startsWith('blob:')) URL.revokeObjectURL(sourceUrl.value)
    file.value = null; sourceUrl.value = ''; originName.value = ''; sourceBlobName.value = ''; resultBlobName.value = ''
    fileKey = ''; digest = ''; operateRecordId.value = 0; progress.value = 0; status.value = 'idle'; errorKey.value = ''
    submissionUnknown.value = false; pollingInterrupted.value = false; validating.value = false
    dimensions.value = { width: 0, height: 0 }; isHighResolutionSource.value = false
    cancelPendingFile()
  }
  function reset() { if (!isBusy.value) clearTask() }
  function cancelPendingFile() { pendingFile = null; pendingDigest = ''; pendingHighResolution.value = null }

  function loadConfig(): Promise<void> {
    if (!identity.value) return Promise.resolve()
    if (configPromise) return configPromise
    const scope = identity.value
    configLoading.value = true
    configError.value = false
    const signal = configController.signal
    const promise = (async () => {
      try {
        const [promptResponse, billingResponse] = await Promise.all([ImageServer.prompts(signal), ImageServer.billing(signal)])
        if (signal.aborted || scope !== identity.value) return
        if (String(promptResponse.code) !== '1' || String(billingResponse.code) !== '1') throw new Error('config')
        options.value = parseImagePromptOptions(promptResponse)
        profiles.value = billingResponse.data || {}
        for (const group of Object.keys(options.value) as ImagePromptGroup[]) {
          if (!options.value[group].some(option => option.value === selected.value[group])) selected.value[group] = options.value[group][0]?.value || ''
        }
        normalizeRepaint()
      } catch {
        if (scope === identity.value && !signal.aborted) { configError.value = true; profiles.value = {}; options.value = emptyImageOptions() }
      } finally {
        if (scope === identity.value && !signal.aborted) { configLoading.value = false; configLoaded.value = true }
      }
    })()
    configPromise = promise
    void promise.finally(() => { if (configPromise === promise) configPromise = null })
    return promise
  }
  function initialize() { initialized = true; if (!configLoaded.value) void loadConfig() }
  function normalizeRepaint() {
    const option = options.value.repaint.find(item => item.value === selected.value.repaint)
    if (!isHighResolutionSource.value && option?.semantic === 'high_resolution') selected.value.repaint = options.value.repaint.find(item => item.semantic !== 'high_resolution')?.value || ''
  }

  watch(identity, () => {
    clearTask()
    configController.abort(); configController = new AbortController(); configPromise = null
    profiles.value = {}; options.value = emptyImageOptions(); configLoaded.value = false; configLoading.value = false; configError.value = false
    selected.value = { scene: '', fidelity: '', repaint: '' }; ocrEnhance.value = false
    if (initialized) void loadConfig()
  }, { flush: 'sync' })
  watch(() => selected.value.repaint, normalizeRepaint, { flush: 'sync' })
  onScopeDispose(() => { clearTask(); configController.abort() })

  async function selectFile(next: File) {
    if (!canUpload.value) return
    errorKey.value = ''
    validating.value = true
    const generation = epoch
    let preview = ''
    try {
      if (!imageMime(next.name)) throw new Error('image2_err_type')
      if (next.size <= 0 || next.size > IMAGE_MAX_BYTES) throw new Error('bizImage_err_size')
      const bytes = await next.arrayBuffer()
      if (!current(generation)) return
      validateImageBytes(next.name, next.size, new Uint8Array(bytes, 0, Math.min(16, bytes.byteLength)))
      preview = URL.createObjectURL(next)
      const image = new Image()
      image.src = preview
      await image.decode()
      if (!current(generation)) return
      const size = { width: image.naturalWidth, height: image.naturalHeight }
      const high = validateImageDimensions(size.width, size.height)
      const profile = profiles.value[imageEntitlementKey(team.value) || '']
      const ext = next.name.split('.').pop()!.toLowerCase()
      const types = String(profile?.fileTypes || '').toLowerCase().split(',').map(value => value.trim().replace(/^\./, ''))
      if (String(profile?.fileTypes || '').trim() && !types.includes(ext)) throw new Error('bizImage_err_format')
      if (Number(profile?.transFileMaxSize) > 0 && next.size > Number(profile.transFileMaxSize) * 1024 * 1024) throw new Error('bizImage_err_size')
      const md5 = CryptoJS.MD5(CryptoJS.lib.WordArray.create(bytes as any)).toString(CryptoJS.enc.Hex)
      let remember = false
      try { remember = localStorage.getItem(HIGH_RESOLUTION_PREFERENCE) === 'true' } catch { /* 存储不可用不影响上传。 */ }
      const hasHigh = options.value.repaint.some(item => item.semantic === 'high_resolution')
      if (high && hasHigh && !remember) {
        pendingFile = next; pendingDigest = md5; pendingHighResolution.value = size
      } else {
        acceptFile(next, md5, size, high && hasHigh && remember)
      }
    } catch (error) {
      if (current(generation)) errorKey.value = error instanceof Error && /^(bizImage_|image2_)/.test(error.message) ? error.message : 'bizImage_err_decode'
    } finally {
      if (preview) URL.revokeObjectURL(preview)
      // acceptFile 已切换代次并清理 validating；失败仍需解除本次读取锁。
      if (generation === epoch) validating.value = false
    }
  }
  function acceptFile(next: File, md5: string, size: { width: number; height: number }, high: boolean) {
    clearTask()
    file.value = next; originName.value = next.name; digest = md5; dimensions.value = size
    isHighResolutionSource.value = validateImageDimensions(size.width, size.height)
    if (high) selected.value.repaint = options.value.repaint.find(item => item.semantic === 'high_resolution')?.value || selected.value.repaint
    else normalizeRepaint()
    sourceUrl.value = URL.createObjectURL(next)
    void upload()
  }
  function chooseHighResolution(high: boolean, remember = false) {
    if (!pendingFile || !pendingHighResolution.value || isBusy.value) return
    const next = pendingFile; const md5 = pendingDigest; const size = pendingHighResolution.value
    if (high && remember) { try { localStorage.setItem(HIGH_RESOLUTION_PREFERENCE, 'true') } catch { /* 可继续本次选择。 */ } }
    acceptFile(next, md5, size, high)
    if (!high) selected.value.repaint = options.value.repaint.find(item => item.semantic !== 'high_resolution')?.value || ''
  }
  async function upload() {
    if (!file.value || blockedKey.value || status.value === 'uploading') return
    const generation = epoch
    const next = file.value
    const signal = taskController.signal
    status.value = 'uploading'; progress.value = 0; errorKey.value = ''
    try {
      const pre = await ImageServer.preUpload({ fileRealName: next.name, freeTag: 0, imageFlag: 1, key: `enterprise-image-${generation}` }, signal)
      if (!current(generation)) return
      if (String(pre.code) !== '1' || !pre.data?.awsUploadUrl || !pre.data?.blobFileName) throw new Error('upload')
      const mime = imageMime(next.name)!
      if (pre.data.contentType && pre.data.contentType !== mime) throw new Error('mime')
      sourceBlobName.value = pre.data.blobFileName
      await putEnterpriseImage(pre.data.awsUploadUrl, next, mime, signal, percent => { if (current(generation)) progress.value = percent })
      if (!current(generation)) return
      const confirm = await ImageServer.confirmUpload({ blobFileName: sourceBlobName.value, fileMd5String: digest, fileRealName: next.name,
        fileSize: next.size, freeTag: 0, imageFlag: 1, key: `enterprise-image-${generation}`, totalPage: 1, sourceType: 0 }, signal)
      if (!current(generation)) return
      if (String(confirm.code) !== '1' || !confirm.data?.tmpFileName) throw new Error('confirm')
      fileKey = confirm.data.tmpFileName; status.value = 'uploaded'; progress.value = 100
    } catch { if (current(generation)) fail('bizImage_err_upload') }
  }

  async function startTranslate() {
    if (!canStart.value) return
    if (pollingInterrupted.value && resultBlobName.value) { resumePoll(); return }
    const generation = epoch
    status.value = 'translating'; progress.value = 0; errorKey.value = ''
    const select = (group: ImagePromptGroup) => options.value[group].find(option => option.value === selected.value[group])!
    const body = { fileKey, sourceLang: sourceLang.value || null, targetLang: targetLang.value, fileFmtType: file.value?.name.split('.').pop()?.toLowerCase(),
      ocrFlag: 1, imageTrans: true, imagePromptWordIds: [select('scene').value, select('fidelity').value, select('repaint').value].join(','),
      sceneType: select('scene').semantic, fidelity: select('fidelity').semantic, renderStrategy: select('repaint').semantic,
      imageWorkflow: select('repaint').semantic === 'high_resolution' ? 'sprite_pro' : 'standard', ocrEnhance: ocrEnhance.value }
    try {
      const response = await ImageServer.translate(body, taskController.signal)
      if (!current(generation)) return
      if (String(response.code) !== '1') { fail('bizImage_err_translate'); return }
      if (!response.data?.blobFileName || !(Number(response.data?.id || response.data?.operateRecordId) > 0)) {
        submissionUnknown.value = true; fail('bizImage_err_submission'); return
      }
      resultBlobName.value = response.data.blobFileName
      operateRecordId.value = Number(response.data.id || response.data.operateRecordId)
      if (!resultUrl.value) { submissionUnknown.value = true; fail('bizImage_err_submission'); return }
      resumePoll()
    } catch {
      if (current(generation)) { submissionUnknown.value = true; fail('bizImage_err_submission') }
    }
  }
  function resumePoll() {
    clearTimeout(timer)
    status.value = 'translating'; errorKey.value = ''; pollingInterrupted.value = false
    const generation = epoch
    const key = imageFileKey(resultBlobName.value)
    const started = Date.now()
    let attempts = 0; let empty = 0
    async function poll() {
      if (!current(generation)) return
      if (++attempts > 900 || Date.now() - started >= 30 * 60 * 1000) { pollingInterrupted.value = true; fail('bizImage_err_pending'); return }
      try {
        const response = await ImageServer.status(key, taskController.signal)
        if (!current(generation)) return
        const verdict = imagePollVerdict(response, key)
        if (verdict.state === 'success') {
          status.value = 'success'; progress.value = 100; resultVersion.value = Date.now()
          void user.getBizTeamInfo()
          return
        }
        if (verdict.state === 'fail') { fail('bizImage_err_translate'); void user.getBizTeamInfo(); return }
        empty = verdict.state === 'empty' ? empty + 1 : 0
        if (verdict.progress !== undefined) progress.value = Math.max(progress.value, verdict.progress)
      } catch { if (!current(generation)) return; empty += 1 }
      if (empty >= 15) { pollingInterrupted.value = true; fail('bizImage_err_pending'); return }
      timer = setTimeout(poll, 2000)
    }
    void poll()
  }
  function retry() {
    if (isBusy.value || blockedKey.value || submissionUnknown.value) return
    if (pollingInterrupted.value && resultBlobName.value) resumePoll()
    else if (fileKey) void startTranslate()
    else void upload()
  }
  function refreshResult() { resultVersion.value = Math.max(Date.now(), resultVersion.value + 1) }

  return { status, progress, file, originName, sourceUrl, sourceBlobName, resultBlobName, resultUrl, operateRecordId, dimensions,
    isHighResolutionSource, pendingHighResolution, sourceLang, targetLang, selected, ocrEnhance, options, configLoading, configReady,
    errorKey, blockedKey, remainingWords, billing, isBusy, canUpload, canStart, initialize, loadConfig, selectFile, startTranslate, retry,
    reset, chooseHighResolution, cancelPendingFile, refreshResult }
})
